import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { MessagingStatus, TelnyxAvailableNumber, TelnyxMessagingStatus, TelnyxSubUsecase } from '../../types'
import { useResolver } from '../../hooks'
import Alert from '../../ui/Alert'
import Button from '../../ui/Button'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { SingleSelect } from '../../ui/form/SingleSelect'
import SwitchField from '../../ui/form/SwitchField'
import Modal, { ModalProps } from '../../ui/Modal'
import { CheckCircleIcon } from '../../ui/icons'
import '../campaign/form/CampaignWizard.css'
import './MessagingSetup.css'

// Maps each status to which of the 4 display steps is active
const DISPLAY_STEP: Record<MessagingStatus, number> = {
    not_configured: 0,
    brand_pending: 1,
    brand_approved: 2,
    campaign_pending: 2,
    campaign_failed: 2,
    campaign_approved: 3,
    number_pending: 3,
    number_failed: 3,
    active: 4,
}

const STEP_LABELS = [
    'messaging_step_brand',
    'messaging_step_vetting',
    'messaging_step_campaign',
    'messaging_step_assign',
]

const ENTITY_TYPES = [
    'PRIVATE_PROFIT',
    'PUBLIC_PROFIT',
    'NON_PROFIT',
    'GOVERNMENT',
]

const VERTICALS = [
    'AGRICULTURE',
    'COMMUNICATION',
    'CONSTRUCTION',
    'EDUCATION',
    'ENERGY',
    'ENTERTAINMENT',
    'FINANCIAL',
    'GAMBLING',
    'GOVERNMENT',
    'HEALTHCARE',
    'HOSPITALITY',
    'HUMAN_RESOURCES',
    'INSURANCE',
    'LEGAL',
    'MANUFACTURING',
    'NGO',
    'POLITICAL',
    'POSTAL',
    'PROFESSIONAL',
    'REAL_ESTATE',
    'RETAIL',
    'TECHNOLOGY',
    'TRANSPORTATION',
]

const RECHECK_INTERVAL = 120

const errorMessage = (err: any): string =>
    err?.response?.data?.error
    ?? err?.error
    ?? err?.message
    ?? 'An unexpected error occurred'

const snakeToDisplay = (value: string) => value
    .split('_')
    .map(w => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')

const StepIndicator = ({ status }: { status: MessagingStatus }) => {
    const { t } = useTranslation()
    const current = DISPLAY_STEP[status] ?? 0

    return (
        <nav className="wizard-steps">
            {STEP_LABELS.map((label, i) => (
                <div
                    key={label}
                    className={`wizard-step${i < current ? ' completed' : ''}${i === current && current < STEP_LABELS.length ? ' active' : ''}`}
                >
                    <span className="wizard-step-label">{t(label)}</span>
                    <div className="wizard-step-bar" />
                </div>
            ))}
        </nav>
    )
}

interface BrandFormValues {
    entityType: string
    displayName: string
    companyName: string
    ein: string
    phone: string
    email: string
    website: string
    street: string
    city: string
    state: string
    postalCode: string
    country: string
    vertical: string
}

interface CampaignFormValues {
    useCase: string
    subUsecases: string[]
    description: string
    sampleMessage1: string
    sampleMessage2: string
    sampleMessage3: string
    messageFlow: string
    helpMessage: string
    optinMessage: string
    optoutMessage: string
    optinKeywords: string
    optoutKeywords: string
    helpKeywords: string
    privacyPolicyLink: string
    termsAndConditionsLink: string
    embeddedLink: boolean
    embeddedPhone: boolean
    ageGated: boolean
}

const SUB_USECASE_REQUIRED = ['MIXED', 'LOW_VOLUME']

const isSpecialUsecase = (usecases: TelnyxSubUsecase[] | null, useCase: string) =>
    usecases?.find(u => u.id === useCase)?.classification === 'SPECIAL'

const SubUsecaseFields = ({ form, usecases }: {
    form: UseFormReturn<CampaignFormValues>
    usecases: TelnyxSubUsecase[] | null
}) => {
    const { t } = useTranslation()
    const useCase = form.watch('useCase')
    const isMixed = SUB_USECASE_REQUIRED.includes(useCase)
    const needsSubUsecases = isMixed || isSpecialUsecase(usecases, useCase)

    if (!needsSubUsecases) return null

    const options = (usecases ?? []).filter(option =>
        option.validSubUsecase !== false
        && option.classification !== 'SPECIAL'
        && !SUB_USECASE_REQUIRED.includes(option.id),
    )

    return (
        <MultiSelect.Field
            form={form}
            name="subUsecases"
            label={t('messaging_campaign_sub_use_cases')}
            subtitle={t('messaging_campaign_sub_use_cases_subtitle')}
            options={options}
            toValue={(option: TelnyxSubUsecase) => option.id}
            getSelectedOptionDisplay={(option: TelnyxSubUsecase) => option.displayName}
            getOptionDisplay={(option: TelnyxSubUsecase) => (
                <span className="messaging-usecase-option">
                    <span className="messaging-usecase-name">{option.displayName}</span>
                    {option.description && (
                        <span className="messaging-usecase-desc">{option.description}</span>
                    )}
                </span>
            )}
            required={isMixed}
        />
    )
}

const CampaignRegistrationForm = ({ defaultValues, providerId, submitLabel, onSubmit }: {
    defaultValues: CampaignFormValues
    providerId: number
    submitLabel: string
    onSubmit: (data: CampaignFormValues) => Promise<void>
}) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [usecases] = useResolver<TelnyxSubUsecase[] | null>(
        useCallback(async () => await api.providers.getUsecases(project.id, 'text', 'telnyx', providerId), [project.id, providerId]),
    )
    return (
        <FormWrapper<CampaignFormValues>
            onSubmit={onSubmit}
            submitLabel={submitLabel}
            defaultValues={defaultValues}
        >
            {form => (
                <>
                    <SingleSelect.Field
                        form={form}
                        name="useCase"
                        label={t('messaging_campaign_use_case')}
                        options={usecases ?? []}
                        toValue={(option: TelnyxSubUsecase) => option.id}
                        getOptionDisplay={(option: TelnyxSubUsecase) => option.displayName}
                        searchable
                        required
                    />
                    <SubUsecaseFields form={form} usecases={usecases} />
                    <TextInput.Field
                        form={form}
                        name="description"
                        label={t('messaging_campaign_description')}
                        subtitle={t('messaging_campaign_description_subtitle')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="messageFlow"
                        label={t('messaging_campaign_message_flow')}
                        subtitle={t('messaging_campaign_message_flow_subtitle')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="sampleMessage1"
                        label={t('messaging_campaign_sample1')}
                        subtitle={t('messaging_campaign_sample_subtitle')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="sampleMessage2"
                        label={t('messaging_campaign_sample2')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="sampleMessage3"
                        label={t('messaging_campaign_sample3')}
                        subtitle={t('messaging_campaign_sample3_subtitle')}
                        textarea
                    />
                    <TextInput.Field
                        form={form}
                        name="optinMessage"
                        label={t('messaging_campaign_optin_message')}
                        subtitle={t('messaging_campaign_optin_message_subtitle')}
                        placeholder={t('messaging_campaign_optin_message_placeholder')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="optoutMessage"
                        label={t('messaging_campaign_optout_message')}
                        subtitle={t('messaging_campaign_optout_message_subtitle')}
                        placeholder={t('messaging_campaign_optout_message_placeholder')}
                        textarea
                        required
                    />
                    <TextInput.Field
                        form={form}
                        name="helpMessage"
                        label={t('messaging_campaign_help_message')}
                        subtitle={t('messaging_campaign_help_message_subtitle')}
                        textarea
                        required
                    />
                    <div className="messaging-form-row">
                        <TextInput.Field
                            form={form}
                            name="optinKeywords"
                            label={t('messaging_campaign_optin_keywords')}
                        />
                        <TextInput.Field
                            form={form}
                            name="optoutKeywords"
                            label={t('messaging_campaign_optout_keywords')}
                        />
                    </div>
                    <TextInput.Field
                        form={form}
                        name="helpKeywords"
                        label={t('messaging_campaign_help_keywords')}
                    />
                    <div className="messaging-form-row">
                        <TextInput.Field
                            form={form}
                            name="privacyPolicyLink"
                            label={t('messaging_campaign_privacy_policy')}
                            subtitle={t('messaging_campaign_privacy_policy_subtitle')}
                            placeholder="https://example.com/privacy"
                        />
                        <TextInput.Field
                            form={form}
                            name="termsAndConditionsLink"
                            label={t('messaging_campaign_terms')}
                            subtitle={t('messaging_campaign_terms_subtitle')}
                            placeholder="https://example.com/terms"
                        />
                    </div>
                    <SwitchField
                        form={form}
                        name="embeddedLink"
                        label={t('messaging_campaign_embedded_link')}
                    />
                    <SwitchField
                        form={form}
                        name="embeddedPhone"
                        label={t('messaging_campaign_embedded_phone')}
                    />
                    <SwitchField
                        form={form}
                        name="ageGated"
                        label={t('messaging_campaign_age_gated')}
                    />
                </>
            )}
        </FormWrapper>
    )
}

const PendingStatus = ({ title, description, isChecking, countdown, error, onCheck }: {
    title: string
    description: string
    isChecking: boolean
    countdown: number
    error?: string
    onCheck: () => void
}) => {
    const { t } = useTranslation()
    return (
        <>
            <Alert title={title} variant="info">
                {description}
            </Alert>
            {error && <Alert title={t('messaging_check_error')} variant="error">{error}</Alert>}
            <div className="messaging-actions">
                <Button
                    onClick={onCheck}
                    isLoading={isChecking}
                >
                    {t('messaging_check_status')}
                </Button>
                <span className="messaging-countdown">
                    {t('messaging_rechecking_in', { seconds: countdown })}
                </span>
            </div>
        </>
    )
}

interface NumberPurchaseProps {
    providerId: number
    onComplete: (result: TelnyxMessagingStatus) => void
}

const NumberPurchase = ({ providerId, onComplete }: NumberPurchaseProps) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [areaCode, setAreaCode] = useState('')
    const [results, setResults] = useState<TelnyxAvailableNumber[]>([])
    const [searched, setSearched] = useState(false)
    const [isSearching, setIsSearching] = useState(false)
    const [purchasing, setPurchasing] = useState<string | null>(null)
    const [error, setError] = useState<{ title: string, message: string } | undefined>()

    const handleSearch = async () => {
        setIsSearching(true)
        setError(undefined)
        try {
            const numbers = await api.providers.searchNumbers(project.id, 'text', 'telnyx', providerId, {
                country_code: 'US',
                national_destination_code: areaCode.trim() || undefined,
                limit: 20,
            })
            setResults(numbers)
            setSearched(true)
        } catch (err) {
            setError({ title: 'messaging_purchase_error_title', message: errorMessage(err) })
            setResults([])
        } finally {
            setIsSearching(false)
        }
    }

    const handlePurchase = async (phoneNumber: string) => {
        if (!confirm(t('messaging_purchase_confirm', { number: phoneNumber }))) return
        setPurchasing(phoneNumber)
        setError(undefined)
        try {
            const result = await api.providers.purchaseNumber(project.id, 'text', 'telnyx', providerId, phoneNumber)
            onComplete(result)
        } catch (err) {
            setError({ title: 'messaging_number_error_title', message: errorMessage(err) })
        } finally {
            setPurchasing(null)
        }
    }

    return (
        <>
            <Alert title={t('messaging_purchase_title')} variant="plain">
                {t('messaging_purchase_intro')}
            </Alert>

            <div className="messaging-number-search">
                <TextInput
                    name="area_code"
                    label={t('messaging_purchase_area_code')}
                    placeholder={t('messaging_purchase_area_code_placeholder')}
                    value={areaCode}
                    onChange={setAreaCode}
                />
                <Button
                    onClick={handleSearch}
                    isLoading={isSearching}
                >
                    {t('messaging_purchase_search')}
                </Button>
            </div>

            {error && <Alert title={t(error.title)} variant="error">{error.message}</Alert>}

            {searched && results.length === 0 && !isSearching && (
                <Alert title={t('messaging_purchase_no_results_title')} variant="info">
                    {t('messaging_purchase_no_results_body')}
                </Alert>
            )}

            {results.length > 0 && (
                <ul className="messaging-number-list">
                    {results.map(n => (
                        <li key={n.phoneNumber}>
                            <div className="messaging-number-info">
                                <span className="messaging-number">{n.vanityFormat ?? n.phoneNumber}</span>
                                {n.region && <span className="messaging-number-meta">{n.region}</span>}
                                {n.monthlyCost && (
                                    <span className="messaging-number-meta">
                                        {t('messaging_purchase_monthly_cost', {
                                            cost: n.monthlyCost,
                                            currency: n.currency ?? 'USD',
                                        })}
                                    </span>
                                )}
                            </div>
                            <Button
                                size="small"
                                onClick={async () => await handlePurchase(n.phoneNumber)}
                                isLoading={purchasing === n.phoneNumber}
                                disabled={!n.reservable || (purchasing !== null && purchasing !== n.phoneNumber)}
                            >
                                {t('messaging_purchase_button')}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </>
    )
}

interface MessagingSetupModalProps extends Omit<ModalProps, 'title'> {
    providerId: number
}

export default function MessagingSetupModal({ providerId, ...props }: MessagingSetupModalProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [isChecking, setIsChecking] = useState(false)
    const [isResetting, setIsResetting] = useState(false)
    const [checkError, setCheckError] = useState<string | undefined>()
    const [countdown, setCountdown] = useState(RECHECK_INTERVAL)
    const countdownRef = useRef<ReturnType<typeof setInterval>>()
    const checkRef = useRef<() => Promise<void>>()

    const [messagingStatus, setMessagingStatus] = useResolver<TelnyxMessagingStatus | null>(
        useCallback(async () => {
            if (!providerId) return null
            return await api.providers.getMessagingStatus(project.id, 'text', 'telnyx', providerId)
        }, [project.id, providerId]),
    )

    const status: MessagingStatus = messagingStatus?.status ?? 'not_configured'

    const brandName = messagingStatus?.brand?.displayName || project.name
    const defaultOptinMessage = t('messaging_campaign_optin_message_default', { brand: brandName })
    const defaultOptoutMessage = t('messaging_campaign_optout_message_default')

    const isPending = status === 'brand_pending'
        || status === 'campaign_pending'
        || status === 'number_pending'
    const isCheckable = isPending || status === 'campaign_failed'

    const checkStatus = async () => {
        if (status === 'brand_pending') {
            return await api.providers.checkBrand(project.id, 'text', 'telnyx', providerId)
        }
        if (status === 'number_pending' || status === 'number_failed') {
            return await api.providers.checkNumber(project.id, 'text', 'telnyx', providerId)
        }
        return await api.providers.checkCampaign(project.id, 'text', 'telnyx', providerId)
    }

    const handleCheckStatus = async () => {
        if (!providerId) return
        setIsChecking(true)
        setCheckError(undefined)
        try {
            setMessagingStatus(await checkStatus())
            setCountdown(RECHECK_INTERVAL)
        } catch (err) {
            setCheckError(errorMessage(err))
        } finally {
            setIsChecking(false)
        }
    }

    checkRef.current = handleCheckStatus

    // The stored status can go stale while the modal is closed (a failed
    // campaign may have been appealed or re-reviewed), so refresh from the
    // carrier once per open in any state that has somewhere to progress to.
    const hasCheckedOnOpen = useRef(false)
    useEffect(() => {
        if (!props.open) {
            hasCheckedOnOpen.current = false
            return
        }
        if (isCheckable && !hasCheckedOnOpen.current) {
            hasCheckedOnOpen.current = true
            void checkRef.current?.()
        }
    }, [props.open, isCheckable])

    useEffect(() => {
        if (!props.open || !isPending) {
            clearInterval(countdownRef.current)
            return
        }

        setCountdown(RECHECK_INTERVAL)
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    void checkRef.current?.()
                    return RECHECK_INTERVAL
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(countdownRef.current)
    }, [props.open, isPending])

    const handleCreateBrand = async (data: BrandFormValues) => {
        if (!providerId) return
        const result = await api.providers.createBrand(project.id, 'text', 'telnyx', providerId, data)
        setMessagingStatus(result)
    }

    const handleCreateCampaign = async (data: CampaignFormValues) => {
        if (!providerId) return
        const result = await api.providers.createCampaign(project.id, 'text', 'telnyx', providerId, data)
        setMessagingStatus(result)
        setCheckError(undefined)
    }

    const handleReset = async () => {
        if (!providerId) return
        if (!confirm(t('messaging_reset_confirm'))) return
        setIsResetting(true)
        try {
            const result = await api.providers.resetMessaging(project.id, 'text', 'telnyx', providerId)
            setMessagingStatus(result)
            setCheckError(undefined)
        } finally {
            setIsResetting(false)
        }
    }

    return (
        <Modal
            {...props}
            title={t('messaging_setup')}
            size="large"
        >
            <div className="messaging-setup">
                <StepIndicator status={status} />

                {status === 'not_configured' && (
                    <>
                        <Alert title={t('messaging_setup')} variant="plain">
                            {t('messaging_setup_intro')}
                        </Alert>
                        <FormWrapper<BrandFormValues>
                            onSubmit={handleCreateBrand}
                            submitLabel={t('messaging_brand_submit')}
                            defaultValues={{
                                entityType: 'PRIVATE_PROFIT',
                                displayName: '',
                                companyName: '',
                                ein: '',
                                phone: '',
                                email: '',
                                website: '',
                                street: '',
                                city: '',
                                state: '',
                                postalCode: '',
                                country: 'US',
                                vertical: 'TECHNOLOGY',
                            }}
                        >
                            {form => (
                                <>
                                    <div className="messaging-form-row">
                                        <SingleSelect.Field
                                            form={form}
                                            name="entityType"
                                            label={t('messaging_brand_entity_type')}
                                            options={ENTITY_TYPES}
                                            getOptionDisplay={snakeToDisplay}
                                            required
                                        />
                                        <SingleSelect.Field
                                            form={form}
                                            name="vertical"
                                            label={t('messaging_brand_vertical')}
                                            options={VERTICALS}
                                            getOptionDisplay={snakeToDisplay}
                                            searchable
                                            required
                                        />
                                    </div>
                                    <div className="messaging-form-row">
                                        <TextInput.Field
                                            form={form}
                                            name="displayName"
                                            label={t('messaging_brand_display_name')}
                                            required
                                        />
                                        <TextInput.Field
                                            form={form}
                                            name="companyName"
                                            label={t('messaging_brand_company_name')}
                                            subtitle={t('messaging_brand_company_name_subtitle')}
                                        />
                                    </div>
                                    <div className="messaging-form-row">
                                        <TextInput.Field
                                            form={form}
                                            name="ein"
                                            label={t('messaging_brand_ein')}
                                            placeholder="12-3456789"
                                            required
                                        />
                                        <TextInput.Field
                                            form={form}
                                            name="phone"
                                            label={t('messaging_brand_phone')}
                                            placeholder="+15551234567"
                                            required
                                        />
                                    </div>
                                    <div className="messaging-form-row">
                                        <TextInput.Field
                                            form={form}
                                            name="email"
                                            label={t('messaging_brand_email')}
                                            type="email"
                                            required
                                        />
                                        <TextInput.Field
                                            form={form}
                                            name="website"
                                            label={t('messaging_brand_website')}
                                            placeholder="https://example.com"
                                            required
                                        />
                                    </div>
                                    <TextInput.Field
                                        form={form}
                                        name="street"
                                        label={t('messaging_brand_street')}
                                        required
                                    />
                                    <div className="messaging-form-row">
                                        <TextInput.Field
                                            form={form}
                                            name="city"
                                            label={t('messaging_brand_city')}
                                            required
                                        />
                                        <TextInput.Field
                                            form={form}
                                            name="state"
                                            label={t('messaging_brand_state')}
                                            required
                                        />
                                    </div>
                                    <div className="messaging-form-row">
                                        <TextInput.Field
                                            form={form}
                                            name="postalCode"
                                            label={t('messaging_brand_postal_code')}
                                            required
                                        />
                                        <SingleSelect.Field
                                            form={form}
                                            name="country"
                                            label={t('messaging_brand_country')}
                                            options={['US', 'CA']}
                                            required
                                        />
                                    </div>
                                </>
                            )}
                        </FormWrapper>
                    </>
                )}

                {status === 'brand_pending' && (
                    <PendingStatus
                        title={t('messaging_vetting_pending_title')}
                        description={t('messaging_vetting_pending')}
                        isChecking={isChecking}
                        countdown={countdown}
                        error={checkError}
                        onCheck={handleCheckStatus}
                    />
                )}

                {status === 'brand_approved' && (
                    <>
                        <Alert title={t('messaging_vetting_approved')} variant="success">
                            {t('messaging_brand_approved_desc')}
                        </Alert>
                        <CampaignRegistrationForm
                            onSubmit={handleCreateCampaign}
                            providerId={providerId}
                            submitLabel={t('messaging_campaign_submit')}
                            defaultValues={{
                                useCase: 'MARKETING',
                                subUsecases: [],
                                description: '',
                                sampleMessage1: '',
                                sampleMessage2: '',
                                sampleMessage3: '',
                                messageFlow: '',
                                helpMessage: '',
                                optinMessage: defaultOptinMessage,
                                optoutMessage: defaultOptoutMessage,
                                optinKeywords: 'START',
                                optoutKeywords: 'STOP',
                                helpKeywords: 'HELP',
                                privacyPolicyLink: '',
                                termsAndConditionsLink: '',
                                embeddedLink: false,
                                embeddedPhone: false,
                                ageGated: false,
                            }}
                        />
                    </>
                )}

                {status === 'campaign_failed' && (
                    <>
                        <Alert title={t('messaging_campaign_failed_title')} variant="error">
                            {t('messaging_campaign_failed_desc')}
                            <p className="messaging-failure-reason">
                                {messagingStatus?.campaign?.failureReasons
                                    ? t('messaging_campaign_failed_reason', {
                                        reason: messagingStatus.campaign.failureReasons,
                                    })
                                    : t('messaging_campaign_failed_no_reason')}
                            </p>
                        </Alert>
                        <CampaignRegistrationForm
                            onSubmit={handleCreateCampaign}
                            providerId={providerId}
                            submitLabel={t('messaging_campaign_resubmit')}
                            defaultValues={{
                                useCase: messagingStatus?.campaign?.useCase ?? 'MARKETING',
                                subUsecases: messagingStatus?.campaign?.subUsecases ?? [],
                                description: messagingStatus?.campaign?.description ?? '',
                                sampleMessage1: messagingStatus?.campaign?.sampleMessage1 ?? '',
                                sampleMessage2: messagingStatus?.campaign?.sampleMessage2 ?? '',
                                sampleMessage3: messagingStatus?.campaign?.sampleMessage3 ?? '',
                                messageFlow: messagingStatus?.campaign?.messageFlow ?? '',
                                helpMessage: messagingStatus?.campaign?.helpMessage ?? '',
                                optinMessage: messagingStatus?.campaign?.optinMessage || defaultOptinMessage,
                                optoutMessage: messagingStatus?.campaign?.optoutMessage || defaultOptoutMessage,
                                optinKeywords: messagingStatus?.campaign?.optinKeywords ?? 'START',
                                optoutKeywords: messagingStatus?.campaign?.optoutKeywords ?? 'STOP',
                                helpKeywords: messagingStatus?.campaign?.helpKeywords ?? 'HELP',
                                privacyPolicyLink: messagingStatus?.campaign?.privacyPolicyLink ?? '',
                                termsAndConditionsLink: messagingStatus?.campaign?.termsAndConditionsLink ?? '',
                                embeddedLink: messagingStatus?.campaign?.embeddedLink ?? false,
                                embeddedPhone: messagingStatus?.campaign?.embeddedPhone ?? false,
                                ageGated: messagingStatus?.campaign?.ageGated ?? false,
                            }}
                        />
                    </>
                )}

                {status === 'campaign_pending' && (
                    <PendingStatus
                        title={t('messaging_campaign_pending_title')}
                        description={t('messaging_campaign_pending')}
                        isChecking={isChecking}
                        countdown={countdown}
                        error={checkError}
                        onCheck={handleCheckStatus}
                    />
                )}

                {status === 'campaign_approved' && (
                    <NumberPurchase
                        providerId={providerId}
                        onComplete={setMessagingStatus}
                    />
                )}

                {status === 'number_pending' && (
                    <PendingStatus
                        title={t('messaging_number_pending_title')}
                        description={t('messaging_number_pending', {
                            number: messagingStatus?.numberAssignment?.phoneNumber ?? '',
                        })}
                        isChecking={isChecking}
                        countdown={countdown}
                        error={checkError}
                        onCheck={handleCheckStatus}
                    />
                )}

                {status === 'number_failed' && (
                    <>
                        <Alert title={t('messaging_number_failed_title')} variant="error">
                            {t('messaging_number_failed_desc', {
                                number: messagingStatus?.numberAssignment?.phoneNumber ?? '',
                            })}
                            {messagingStatus?.numberAssignment?.failureReasons && (
                                <p className="messaging-failure-reason">
                                    {t('messaging_campaign_failed_reason', {
                                        reason: messagingStatus.numberAssignment.failureReasons,
                                    })}
                                </p>
                            )}
                        </Alert>
                        {checkError && <Alert title={t('messaging_check_error')} variant="error">{checkError}</Alert>}
                        <div className="messaging-actions">
                            <Button
                                onClick={handleCheckStatus}
                                isLoading={isChecking}
                            >
                                {t('messaging_number_retry')}
                            </Button>
                        </div>
                    </>
                )}

                {status === 'active' && (
                    <div className="messaging-success">
                        <CheckCircleIcon />
                        <Alert title={t('messaging_complete')} variant="success">
                            {t('messaging_complete_desc', {
                                number: messagingStatus?.numberAssignment?.phoneNumber ?? '',
                            })}
                        </Alert>
                    </div>
                )}

                {status !== 'not_configured' && status !== 'active' && (
                    <div className="messaging-reset">
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={handleReset}
                            isLoading={isResetting}
                        >
                            {t('messaging_reset_button')}
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    )
}
